import { Header } from '@components/Header';
import { PageErrorState } from '@components/core-ui/PageErrorState';
import { PagePagination } from '@components/core-ui/Pagination';
import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import EditIcon from '@components/icons/Edit.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { usePageContext } from '@pages/Admin/SSOProviders/providers/PageProvider';
import { PrismaSSOProviderDto } from 'app/API';
import { CellContent } from './styles/CellContent';
import { Wrapper } from './styles/Wrapper';

const enforcementSummary = (provider: PrismaSSOProviderDto): string => {
  const flags = [
    provider.enforceSsoOnlyAuthentication ? 'SSO-only' : null,
    provider.requiresPreProvisionedUser ? 'Pre-provisioned' : null,
    provider.trustProviderEmailVerification ? 'Trusts email' : null,
  ].filter(Boolean);
  return flags.length ? flags.join(', ') : '—';
};

export const Content = () => {
  const {
    isLoading,
    hasInitialError,
    retryProviders,
    page,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    ssoProviders,
    exportCSV,
    openCreateModal,
    openEditModal,
    removeProvider,
  } = usePageContext();

  return (
    <>
      <Header
        title={'SSO Providers'}
        buttonLabel={'Add Provider'}
        buttonOnClick={openCreateModal}
        secondButtonLabel={ssoProviders.data?.length ? 'Export CSV' : undefined}
        secondButtonOnClick={ssoProviders.data?.length ? () => exportCSV() : undefined}
        setSearch={setSearch}
        search={search}
      />

      <LinearProgressLoader active={isLoading} />
      <Wrapper>
        {hasInitialError ? (
          <PageErrorState
            message="Couldn't load SSO providers"
            actionLabel="Retry"
            actionDisabled={isLoading}
            onAction={retryProviders}
          />
        ) : (
          <>
            <TableWrapper>
              <Table>
                <Thead>
                  <Tr>
                    <Th>#</Th>
                    <Th>Domains</Th>
                    <Th>Email Routes</Th>
                    <Th>Cognito Identity Provider</Th>
                    <Th>Type</Th>
                    <Th>Linked Org</Th>
                    <Th>Enforcement</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {ssoProviders.data?.map((provider, index) => (
                    <Tr key={provider.id ?? index}>
                      <Td>
                        <CellContent>{(page - 1) * pageSize + index + 1}</CellContent>
                      </Td>
                      <Td>
                        <CellContent>{provider.domains.join(', ') || '—'}</CellContent>
                      </Td>
                      <Td>
                        <CellContent>{provider.emails.join(', ') || '—'}</CellContent>
                      </Td>
                      <Td>
                        <CellContent>{provider.identityProvider}</CellContent>
                      </Td>
                      <Td>
                        <CellContent>{provider.type}</CellContent>
                      </Td>
                      <Td>
                        <CellContent>{provider.organizationId ? 'Linked' : '—'}</CellContent>
                      </Td>
                      <Td>
                        <CellContent>{enforcementSummary(provider)}</CellContent>
                      </Td>
                      <Td>
                        <CellContent>
                          <button
                            type="button"
                            aria-label="Edit"
                            title="Edit"
                            className="mr-2 inline-flex cursor-pointer items-center border-0 bg-transparent p-1"
                            onClick={() => openEditModal(provider)}
                          >
                            <EditIcon />
                          </button>
                          <IconButtonDelete title="Remove" onClick={() => removeProvider(provider)}>
                            <TrashBin />
                          </IconButtonDelete>
                        </CellContent>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableWrapper>
            <PagePagination
              totalItemsCount={ssoProviders.count ?? 0}
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          </>
        )}
      </Wrapper>
    </>
  );
};
